import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

type Props = {
  readonly jobId: Id<'jobs'>
}

type KitQuestionsProps = {
  readonly kitId: Id<'interviewKits'>
}

const KitQuestions = ({ kitId }: KitQuestionsProps) => {
  const [newQuestion, setNewQuestion] = useState('')

  const { data: questions = [] } = useQuery(
    convexQuery(api.features.jobs.interview_kits.listMyInterviewKitQuestions, {
      kitId,
    }),
  )

  const { mutateAsync: addQuestion } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.interview_kits.addQuestionToMyInterviewKit,
    ),
  })

  const { mutateAsync: deleteQuestion } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.interview_kits.deleteMyInterviewKitQuestion,
    ),
  })

  const handleAddQuestion = async (): Promise<void> => {
    const trimmed = newQuestion.trim()
    if (!trimmed) return
    await addQuestion({ kitId, question: trimmed, order: questions.length })
    setNewQuestion('')
  }

  return (
    <div className="flex flex-col gap-2 pl-4">
      {questions.length === 0 ? (
        <p className="text-xs text-primary/40">No questions yet.</p>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {questions.map((q, index) => (
            <li
              key={q._id}
              className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-1.5"
            >
              <span className="text-sm text-primary/70">{index + 1}.</span>
              <span className="flex-1 text-sm text-primary">{q.question}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void deleteQuestion({ questionId: q._id })}
              >
                Remove
              </Button>
            </li>
          ))}
        </ol>
      )}
      <div className="flex gap-2">
        <Input
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
          placeholder="Add a question..."
          className="text-sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleAddQuestion()
          }}
        />
        <Button
          size="sm"
          onClick={() => void handleAddQuestion()}
          disabled={!newQuestion.trim()}
        >
          Add
        </Button>
      </div>
    </div>
  )
}

export const InterviewKitsEditor = ({ jobId }: Props) => {
  const [newKitTitle, setNewKitTitle] = useState('')
  const [expandedKitId, setExpandedKitId] = useState<
    Id<'interviewKits'> | undefined
  >(undefined)

  const { data: kits = [] } = useQuery(
    convexQuery(api.features.jobs.interview_kits.listMyInterviewKitsByJobId, {
      jobId,
    }),
  )

  const { mutateAsync: createKit } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.interview_kits.createMyInterviewKit,
    ),
  })

  const { mutateAsync: deleteKit } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.interview_kits.deleteMyInterviewKit,
    ),
  })

  const handleCreateKit = async (): Promise<void> => {
    const trimmed = newKitTitle.trim()
    if (!trimmed) return
    await createKit({ jobId, title: trimmed })
    setNewKitTitle('')
  }

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-sm font-medium text-primary/70">Interview kits</h3>

      {kits.length === 0 ? (
        <p className="text-sm text-primary/40">No interview kits yet.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {kits.map((kit) => (
            <li
              key={kit._id}
              className="flex flex-col gap-2 rounded-md border border-border p-3"
            >
              <div className="flex items-center justify-between gap-4">
                <button
                  type="button"
                  className="flex-1 text-left text-sm font-medium text-primary hover:text-primary/80"
                  onClick={() =>
                    setExpandedKitId(
                      expandedKitId === kit._id ? undefined : kit._id,
                    )
                  }
                >
                  {kit.title}
                </button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (expandedKitId === kit._id) setExpandedKitId(undefined)
                    void deleteKit({ kitId: kit._id })
                  }}
                >
                  Delete
                </Button>
              </div>
              {expandedKitId === kit._id && <KitQuestions kitId={kit._id} />}
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <Input
          value={newKitTitle}
          onChange={(e) => setNewKitTitle(e.target.value)}
          placeholder="New kit title..."
          className="text-sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleCreateKit()
          }}
        />
        <Button
          size="sm"
          onClick={() => void handleCreateKit()}
          disabled={!newKitTitle.trim()}
        >
          Create
        </Button>
      </div>
    </div>
  )
}
